import {AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, Input} from '@angular/core';
import {marker as _} from '@biesbjerg/ngx-translate-extract-marker';
import {PaginationInstance} from 'ngx-pagination';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {debounceTime, delay, finalize, map, take as rxjsTake, takeUntil, tap} from 'rxjs/operators';
import {
  DataService,
  QueryResult,
  Dialog,
  NotificationService,
  AssetLike,
  AssetSearchInputComponent,
  AssetGalleryComponent,
  Asset,
  // CreateAssetsMutation,
  GetAssetListQuery,
  GetAssetListQueryVariables,
  LogicalOperator,
  SortOrder,
  TagFragment,
  ModalService,
} from '@vendure/admin-ui/core';
import {CustomAssetService} from '../../asset/asset.service';
import {OversizeImagesDialogComponent} from '../oversize-images-dialog/oversize-images-dialog.component';

/**
 * @description
 * A dialog which allows the creation and selection of assets.
 *
 * @example
 * ```TypeScript
 * selectAssets() {
 *   this.modalService
 *     .fromComponent(AssetPickerDialogComponent, {
 *         size: 'xl',
 *     })
 *     .subscribe(result => {
 *         if (result && result.length) {
 *             // ...
 *         }
 *     });
 * }
 * ```
 *
 * @docsCategory components
 */
@Component({
  selector: 'app-asset-picker-dialog',
  templateUrl: './asset-picker-dialog.component.html',
  styleUrls: ['./asset-picker-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetPickerDialogComponent implements OnInit, AfterViewInit, OnDestroy, Dialog<Asset[]> {
  assets$: Observable<AssetLike[]>;
  allTags$: Observable<TagFragment[]>;
  paginationConfig: PaginationInstance = {
    currentPage: 1,
    itemsPerPage: 25,
    totalItems: 1,
  };
  @Input() pickType: string;
  @ViewChild('assetSearchInputComponent')
  private assetSearchInputComponent: AssetSearchInputComponent;
  @ViewChild('assetGalleryComponent')
  private assetGalleryComponent: AssetGalleryComponent;

  multiSelect = true;
  initialTags: string[] = [];

  resolveWith: (result?: Asset[]) => void;
  selection: Asset[] = [];
  searchTerm$ = new BehaviorSubject<string | undefined>(undefined);
  filterByTags$ = new BehaviorSubject<TagFragment[] | undefined>(undefined);
  uploading = false;
  private listQuery: QueryResult<GetAssetListQuery, GetAssetListQueryVariables>;
  private destroy$ = new Subject<void>();

  constructor(
    private dataService: DataService,
    private notificationService: NotificationService,
    private assetService: CustomAssetService,
    protected modalService: ModalService,
  ) {}

  ngOnInit() {
    this.listQuery = this.dataService.product.getAssetList(this.paginationConfig.itemsPerPage, 0);
    this.allTags$ = this.dataService.product.getTagList().mapSingle(data => data.tags.items);
    this.assets$ = this.listQuery.stream$.pipe(
      tap(result => (this.paginationConfig.totalItems = result.assets.totalItems)),
      map(result => result.assets.items),
    );
    this.searchTerm$.pipe(debounceTime(250), takeUntil(this.destroy$)).subscribe(() => {
      this.fetchPage(this.paginationConfig.currentPage, this.paginationConfig.itemsPerPage);
    });
    this.filterByTags$.pipe(debounceTime(100), takeUntil(this.destroy$)).subscribe(() => {
      this.fetchPage(this.paginationConfig.currentPage, this.paginationConfig.itemsPerPage);
    });
  }

  ngAfterViewInit() {
    if (0 < this.initialTags.length) {
      this.allTags$
        .pipe(
          rxjsTake(1),
          map(allTags => allTags.filter(tag => this.initialTags.includes(tag.value))),
          tap(tags => this.filterByTags$.next(tags)),
          delay(1),
        )
        .subscribe(tags => this.assetSearchInputComponent.setTags(tags));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  pageChange(page: number) {
    this.paginationConfig.currentPage = page;
    this.fetchPage(this.paginationConfig.currentPage, this.paginationConfig.itemsPerPage);
  }

  itemsPerPageChange(itemsPerPage: number) {
    this.paginationConfig.itemsPerPage = itemsPerPage;
    this.fetchPage(this.paginationConfig.currentPage, this.paginationConfig.itemsPerPage);
  }

  cancel() {
    this.resolveWith();
  }

  select() {
    this.resolveWith(this.selection);
  }

  async createAssets(selectFiles: File[]) {
    // const files = await this.assetService.checkSizeAndTip(selectFiles);
    if (!selectFiles?.length) {
      return;
    }
    this.uploading = true;
    const files = await this.assetService.checkSizeAndCompressImageBeforeUpload(selectFiles);

    const filters = files.reduce(
      (r, f) => {
        if (f.compressed.size > this.assetService.curConvertSize()) {
          r.oversize.push(f.origin);
        } else {
          r.qualified.push(f.compressed);
        }
        return r;
      },
      {qualified: [] as File[], oversize: [] as File[]},
    );

    // 如果有超过大小的文件，显示对话框
    if (filters.oversize.length > 0) {
      this.modalService
        .fromComponent(OversizeImagesDialogComponent, {
          locals: {files: filters.oversize},
          size: 'md',
          closable: true,
        })
        .subscribe();
    }

    // const files = await this.assetService.checkSizeAndCompressImageBeforeUpload(fs);
    // console.log(files);
    if (filters.qualified.length) {
      // console.log(files);
      this.dataService.product
        .createAssets(filters.qualified)
        .pipe(finalize(() => (this.uploading = false)))
        .subscribe(res => {
          this.fetchPage(this.paginationConfig.currentPage, this.paginationConfig.itemsPerPage);
          this.notificationService.success(_('asset.notify-create-assets-success'), {
            count: files.length,
          });
          const assets = res.createAssets.filter(a => a.__typename === 'Asset') as AssetLike[];
          this.assetGalleryComponent.selectMultiple(assets);
        });
    }
  }

  private fetchPage(currentPage: number, itemsPerPage: number) {
    console.log('fetch page');
    const take = +itemsPerPage;
    const skip = (currentPage - 1) * +itemsPerPage;
    const searchTerm = this.searchTerm$.value;
    const tags = this.filterByTags$.value?.map(t => t.value);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.listQuery.ref.refetch({
      options: {
        skip,
        take,
        filter: {
          name: {
            contains: searchTerm,
          },
          type: this.pickType
            ? {
                eq: this.pickType,
              }
            : undefined,
        },
        sort: {
          createdAt: SortOrder.DESC,
        },
        tags,
        tagsOperator: LogicalOperator.AND,
      },
    });
  }
}
